import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Sparkles, Loader2, Trash2, BarChart3, Menu, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import ChatBubble from "@/components/ChatBubble";
import ChatList, { ProfileTriggerRow } from "@/components/ChatList";
import ProfilePanel, { STYLE_KEY_TO_INTERNAL } from "@/components/ProfilePanel";
import DateDivider from "@/components/DateDivider";
import MicroLine from "@/components/MicroLine";
import AnalysisCard from "@/components/AnalysisCard";
import AnalyzingIndicator from "@/components/AnalyzingIndicator";
import IntentPresets, { INTENTS } from "@/components/IntentPresets";
import IntentStarters, { STARTERS } from "@/components/IntentStarters";
import MicButton from "@/components/MicButton";
import RegisterSelector from "@/components/RegisterSelector";
import { INTENT_TO_STYLE } from "@/lib/intentMeta";
import { detectPerspective } from "@/lib/perspective";
import { useAuth } from "@/lib/authContext";
import { saveChatContacts } from "@/lib/sendApi";
import { STYLE_LABELS } from "@/lib/insights";
import { dayKeyFor, dayLabelFor } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAnalyzingSound } from "@/lib/useAnalyzingSound";
import { useVoiceInput } from "@/lib/useVoiceInput";
import { track, trackSessionStart, setPrivacyMode } from "@/lib/analytics";
import {
    logRewriteSession,
    clearSessionLog,
    setStyleChangeDate,
    isPositiveSignal,
} from "@/lib/sessionLog";
import { buildExportPDF, exportFilenameStem } from "@/lib/exportReport";
import { deleteCloudState } from "@/lib/cloudSync";
import {
    loadStore,
    saveStore,
    createChat,
    renameChat as renameChatStore,
    deleteChat as deleteChatStore,
    setActiveChat,
    updateChatMessages,
    updateChatField,
    getActiveChat,
} from "@/lib/chats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PERSIST_MESSAGES = 200;

// Calm, scenario-grounded placeholder. ToneGuard positions itself as a
// companion for messages, emails, and difficult conversations — not a
// document editor. Short examples keep the cursor inviting without
// implying long-form writing.
const PLACEHOLDER_TEXT =
    "Ideal for messages, emails, and difficult conversations.";

// Soft comfort ceiling for the composer. Below this we say nothing; above it
// we surface a quiet word counter so the user knows the system is still
// happy, but starts approaching its sweet-spot limit.
const WORD_COMFORT_LIMIT = 350;
const WORD_HINT_THRESHOLD = 250;

// Hard ceiling enforced on both client and server. Backend rejects >4000
// chars; 4000 ≈ ~600 words which is well past the 350-word comfort target.
const MAX_INPUT_CHARS = 4000;

const ToneGuardPage = () => {
    // ------------------------------------------------------------------
    // Multi-chat store. The page holds one "store" snapshot in state and
    // derives the active chat's messages/preferredStyle/sessionId from it.
    // ------------------------------------------------------------------
    const [store, setStore] = useState(() => loadStore());

    const activeChat = useMemo(() => getActiveChat(store), [store]);
    const activeChatId = store.activeChatId;
    const messages = activeChat?.messages || [];
    const preferredStyle = activeChat?.preferredStyle || null;
    const sessionId = activeChat?.sessionId || "tg-empty";

    // Per-chat wrappers — keep the call sites of the original code
    // (`setMessages(arr)` / `setMessages(prev => …)`) identical.
    const setMessages = useCallback(
        (updaterOrValue) => {
            setStore((prev) => {
                const id = prev.activeChatId;
                if (!id) return prev;
                const cur =
                    prev.chats.find((c) => c.id === id)?.messages || [];
                const next =
                    typeof updaterOrValue === "function"
                        ? updaterOrValue(cur)
                        : updaterOrValue;
                return updateChatMessages(prev, id, next);
            });
        },
        [],
    );
    const setPreferredStyle = useCallback((style) => {
        setStore((prev) => {
            const id = prev.activeChatId;
            if (!id) return prev;
            return updateChatField(prev, id, { preferredStyle: style });
        });
    }, []);

    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    // Auth — wired in for the existing footer "Log out" button + account email display.
    const { user: authUser, logout: authLogout } = useAuth();
    // Micro-line rotation token — bumped on chat switch AND on rewrite-card
    // dismissal so the reflective line refreshes at both natural pauses.
    const [microLineToken, setMicroLineToken] = useState(0);
    // Progressive disclosure for the intent row. Default collapsed = just a
    // single "Intent" pill above the input. Tapping the pill toggles the
    // full chip strip + suggestion chips visible. Keeps the composer area
    // calm by default without hiding the option.
    const [intentExpanded, setIntentExpanded] = useState(false);
    // Lifted-up panel state — both the ChatList "+ New" / empty-state buttons
    // AND the DesktopEmptyState "Start a new chat" CTA flip this to open the
    // chat-creation panel. Single source of truth so the creation flow stays
    // consistent across entry points and event objects can't leak in.
    const [creating, setCreating] = useState(false);
    const handleRequestCreate = useCallback(() => setCreating(true), []);
    const handleCloseCreate = useCallback(() => setCreating(false), []);
    const [soundEnabled, setSoundEnabled] = useState(
        () => store.soundEnabled === true,
    );
    const [intent, setIntent] = useState(() => store.lastIntent || null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [defaultStyle, setDefaultStyleState] = useState(() => {
        const stored = store.defaultStyle;
        const validKeys = ["calm", "direct", "warm", "professional"];
        return validKeys.includes(stored) ? stored : null;
    });
    const [userProfile, setUserProfileState] = useState(
        () => store.userProfile || { name: "Friend", photo: null },
    );

    // Derive a friendly display name from an email. Local-part before `@`,
    // split on . _ -, title-cased. e.g. "channu.icm@gmail.com" → "Channu Icm".
    const deriveNameFromEmail = (email) => {
        if (!email || typeof email !== "string") return null;
        const local = email.split("@")[0] || "";
        const parts = local
            .split(/[._-]+/)
            .map((p) => p.trim())
            .filter(Boolean);
        if (!parts.length) return null;
        return parts
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
            .join(" ");
    };

    // Auto-seed the local profile from the authenticated user the FIRST
    // time we see them on this device:
    //   1. Prefer the backend `authUser.name` (always set for Google OAuth
    //      users; null for email/password users who didn't supply a name).
    //   2. Fall back to a name derived from the email local-part
    //      (e.g. "channu.icm@gmail.com" → "Channu Icm").
    //   3. Photo seeds from `authUser.picture` (Google avatar URL).
    // We only overwrite the placeholder default ("Friend") and never clobber
    // a name the user has typed themselves into the Profile panel.
    useEffect(() => {
        if (!authUser?.email) return;
        const looksLikeDefault =
            !userProfile?.name ||
            userProfile.name.trim().toLowerCase() === "friend";
        const derived =
            (authUser.name && authUser.name.trim()) ||
            deriveNameFromEmail(authUser.email);
        if (!derived) return;
        setUserProfileState((prev) => {
            const next = { ...(prev || {}) };
            if (looksLikeDefault) next.name = derived;
            // Photo: only fill if we don't already have a user-uploaded one.
            if (!next.photo && authUser.picture) next.photo = authUser.picture;
            return next;
        });
    }, [authUser?.email, authUser?.name, authUser?.picture]);
    // ToneMirror "communication reflection preferences" — all on by default
    // (best new-user experience). Persists in the v2 store.
    const TM_DEFAULTS = {
        emotionalNudges: true,
        patternTracking: true,
        rewriteComparison: true,
        privacyMode: false,
    };
    const [tonemirrorPrefs, setTonemirrorPrefsState] = useState(() => ({
        ...TM_DEFAULTS,
        ...(store.tonemirrorPrefs || {}),
    }));
    // Mobile UI state for the input row.
    //   - `isTyping` (≥ 1 non-whitespace char) = mobile "typing state" →
    //     hide chips + mic, swap mic icon with the inline Review CTA.
    //   - Idle state (no chars or whitespace-only) → chips + mic visible,
    //     no CTA. Trimming avoids the "dead CTA" case where a single space
    //     would render a disabled button.
    // Desktop ignores both — chips and Review pill always visible.
    const isTyping = draft.trim().length > 0;
    const scrollRef = useRef(null);

    const inputRef = useRef(null);

    // Soft ambient pad while analyzing (Web Audio, no asset).
    useAnalyzingSound(loading || regenerating, soundEnabled);

    // Fire session_started once per page load (anonymous telemetry).
    useEffect(() => {
        trackSessionStart();
    }, []);

    // Mobile state debug (§12) — dev-only sanity log so we can verify the
    // CTA visibility contract holds across input changes. Stripped from
    // production builds.
    useEffect(() => {
        if (process.env.NODE_ENV === "production") return;
        console.log({
            inputLength: draft.length,
            isTyping,
            CTA_visible: isTyping,
        });
    }, [draft, isTyping]);

    // Voice input — browser SpeechRecognition, no backend.
    const voice = useVoiceInput({ lang: "en-US" });
    const draftBeforeRecordingRef = useRef("");

    // Live-update the composer with interim transcript so the user sees
    // what's being captured as they speak.
    useEffect(() => {
        if (!voice.isRecording) return;
        const base = draftBeforeRecordingRef.current;
        const sep = base && !base.endsWith(" ") ? " " : "";
        const live = voice.interim ? (base + sep + voice.interim).trimStart() : base;
        setDraft(live);
    }, [voice.isRecording, voice.interim]);

    // Surface permission / recognition errors as inline toasts (never fail silently).
    useEffect(() => {
        if (!voice.error) return;
        const err = voice.error;
        if (err === "aborted") {
            // user tapped stop — silent, and preserve whatever was transcribed so far.
            voice.reset();
            return;
        }
        if (err === "not-allowed" || err === "service-not-allowed") {
            toast.error("Microphone access needed", {
                description: "Enable mic permission in your browser to use voice input.",
            });
        } else if (err === "no-speech") {
            // No audio captured at all — quiet hint, no revert needed
            // because the composer already shows whatever was in it.
            toast("Couldn't catch that — try again");
            voice.reset();
            return;
        } else if (err === "unsupported") {
            toast.error("Voice input isn't supported in this browser");
        } else {
            toast.error("Voice input stopped — please try again");
        }
        voice.reset();
        // Only revert if the recording produced nothing the user could see.
        // When the composer already contains captured speech (final or
        // interim that we just committed in the hook), keep it so the user
        // can review / edit before sending.
        const captured = draft.trim() !== draftBeforeRecordingRef.current.trim();
        if (!captured) {
            setDraft(draftBeforeRecordingRef.current);
        }
    }, [voice.error]);

    const toggleVoice = () => {
        if (voice.isRecording) {
            voice.stop();
            return;
        }
        if (!voice.isSupported) {
            toast.error("Voice input isn't supported in this browser");
            return;
        }
        draftBeforeRecordingRef.current = draft;
        voice.start((finalText) => {
            // Commit: replace any interim-ized live draft with the final transcript
            // appended to whatever the user had typed before hitting the mic.
            const base = draftBeforeRecordingRef.current;
            const sep = base && !base.endsWith(" ") ? " " : "";
            const next = base ? base + sep + finalText : finalText;
            setDraft(next);
            focusComposerToEnd(next);
        });
    };

    const focusComposerToEnd = (text) => {
        // Defer past Radix DropdownMenu's own focus-restore on close, then place caret at end.
        const apply = () => {
            const el = inputRef.current;
            if (!el) return;
            el.focus();
            try {
                const len = (text || "").length;
                el.setSelectionRange?.(len, len);
            } catch {
                /* noop */
            }
        };
        window.requestAnimationFrame(() =>
            window.requestAnimationFrame(apply),
        );
    };

    const handleBubbleCopy = () => {
        focusComposerToEnd(draft);
    };

    const handleBubbleEdit = (text) => {
        setDraft(text);
        focusComposerToEnd(text);
        toast("Loaded into composer");
    };

    const pickStarter = (text) => {
        setDraft(text);
        focusComposerToEnd(text);
    };

    const handleBubbleDelete = (msg) => {
        if (!msg?.id) return;
        let restored = false;
        let snapshot = [];
        setMessages((prev) => {
            snapshot = prev;
            const idx = prev.findIndex((m) => m.id === msg.id);
            if (idx === -1) return prev;
            const next = prev.slice();
            next.splice(idx, 1);
            return next;
        });
        toast.success("Message deleted", {
            duration: 5000,
            action: {
                label: "Undo",
                onClick: () => {
                    if (restored) return;
                    restored = true;
                    setMessages(snapshot);
                },
            },
        });
    };

    // True if the current composer text is empty OR is exactly one of the
    // intent's pre-baked starter lines (i.e., user hasn't started editing yet).
    const isDraftStillAStarter = (text, activeIntent) => {
        if (!text || text.trim() === "") return true;
        const pool = STARTERS[activeIntent] || [];
        return pool.includes(text);
    };

    const handleIntentChange = (newIntent) => {
        // Toggle off
        if (!newIntent) {
            // If the composer currently holds a previous-intent starter, clear it
            if (intent && isDraftStillAStarter(draft, intent) && draft.trim() !== "") {
                setDraft("");
            }
            setIntent(null);
            return;
        }
        // Toggle on / switch
        const stillStarter = isDraftStillAStarter(draft, intent);
        setIntent(newIntent);
        track("intent_selected", { intent: newIntent });
        if (stillStarter) {
            const pool = STARTERS[newIntent] || [];
            if (pool.length > 0) {
                const pick = pool[Math.floor(Math.random() * pool.length)];
                pickStarter(pick);
            }
        }
    };

    const startersVisible =
        !!intent && isDraftStillAStarter(draft, intent);

    const canAnalyze = draft.trim().length > 0 && !loading;

    // Mirror `privacyMode` to a tiny localStorage flag that analytics.js
    // reads synchronously — keeps the kill-switch decoupled from React state.
    useEffect(() => {
        setPrivacyMode(!!tonemirrorPrefs.privacyMode);
    }, [tonemirrorPrefs.privacyMode]);

    // Persist everything to localStorage in one place. Individual prefs
    // (sound, intent, defaultStyle, tonemirrorPrefs, userProfile) are
    // composed into the persisted snapshot here rather than via a separate
    // mirror-into-store effect — that earlier pattern triggered the
    // set-state-in-effect anti-pattern. The `store` state itself remains
    // ground-truth for chats + activeChatId.
    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            const trimmed = {
                ...store,
                chats: store.chats.map((c) => ({
                    ...c,
                    messages: (c.messages || []).slice(-MAX_PERSIST_MESSAGES),
                })),
                soundEnabled,
                lastIntent: intent || null,
                defaultStyle,
                tonemirrorPrefs: { ...tonemirrorPrefs },
                userProfile: {
                    name: userProfile?.name || "Friend",
                    photo: userProfile?.photo ?? null,
                },
            };
            saveStore(trimmed);
        } catch {
            /* quota / disabled storage — ignore */
        }
    }, [
        store,
        soundEnabled,
        intent,
        defaultStyle,
        tonemirrorPrefs,
        userProfile,
    ]);

    useEffect(() => {
        // Scroll the message viewport (the <main> wrapping chat-feed) so new
        // bubbles appear at the bottom while older ones scroll up internally.
        const inner = scrollRef.current;
        const viewport = inner?.parentElement;
        if (viewport) {
            viewport.scrollTo({
                top: viewport.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [messages.length, analysis, loading]);

    const clearHistory = () => {
        // Clears the ACTIVE chat's messages only (does not delete the chat).
        if (!activeChatId) return;
        setMessages([]);
        setAnalysis(null);
        setDraft("");
        setPreferredStyle(null);
        try {
            window.localStorage.removeItem("toneguard:counters");
            window.localStorage.removeItem("toneguard:shownInsightHashes");
        } catch {
            /* ignore */
        }
        toast.success("Chat cleared");
    };

    // -------------------- Chat-level operations --------------------
    const handleSelectChat = useCallback((id) => {
        setStore((prev) => setActiveChat(prev, id));
        setAnalysis(null);
        setDraft("");
    }, []);

    const handleNewChat = useCallback((name, opts) => {
        // Defensive: callers MUST pass a string name (CreateChatPanel does).
        // If a non-string slipped through (e.g. a React SyntheticEvent from
        // a bare `onClick={handleNewChat}` wiring), fall back to a safe
        // default so the chat is still created instead of crashing the app.
        const safeName =
            typeof name === "string"
                ? name.trim() || "New chat"
                : "New chat";
        const safeOpts =
            opts && typeof opts === "object" && !opts.nativeEvent ? opts : {};

        // Local store update first (instant UX). If contacts were provided,
        // also fire-and-forget the Mongo upsert so they survive cross-device.
        let createdChatId = null;
        setStore((prev) => {
            const next = createChat(prev, safeName, safeOpts);
            createdChatId = next.activeChatId;
            return next;
        });
        if (createdChatId && (safeOpts.phone_number || safeOpts.email_address)) {
            saveChatContacts(createdChatId, {
                phone_number: safeOpts.phone_number || null,
                email_address: safeOpts.email_address || null,
            }).catch(() => {
                /* swallow — local copy is the source of truth for SendPanel */
            });
        }
        setAnalysis(null);
        setDraft("");
        setCreating(false); // close the creation panel after commit
        toast.success("New chat created");
    }, []);

    /** Update contacts for an existing chat (used by ChatContactsRow). */
    const handleUpdateChatContacts = useCallback((id, { phone_number, email_address }) => {
        if (!id) return;
        setStore((prev) =>
            updateChatField(prev, id, {
                phone_number: phone_number || null,
                email_address: email_address || null,
            }),
        );
        saveChatContacts(id, {
            phone_number: phone_number || null,
            email_address: email_address || null,
        }).catch(() => {
            /* see comment above — best-effort sync */
        });
    }, []);

    const handleRenameChat = useCallback((id, name) => {
        setStore((prev) => renameChatStore(prev, id, name));
    }, []);

    const handleSetChatRegister = useCallback((id, register) => {
        if (!id) return;
        // register_confirmed is forced TRUE whenever the user manually
        // picks a register (any path — chat header pill, rename flow, or
        // post-creation update) so the chat creation panel's question
        // never re-fires for this chat (spec: "Register question fires
        // ONCE per chat max").
        setStore((prev) =>
            updateChatField(prev, id, {
                relationship_register: register || null,
                register_confirmed: register ? true : false,
            }),
        );
    }, []);

    const handleDeleteChat = useCallback((id) => {
        setStore((prev) => deleteChatStore(prev, id));
        setAnalysis(null);
        toast.success("Chat deleted");
    }, []);

    // Open-state for the mobile list view. When `null` on mobile, the
    // chat-list takes the full screen; when set, the chat opens.
    // Driven entirely by `activeChatId` — picking a chat opens it,
    // tapping the back arrow clears `activeChatId`.
    const showMobileList = !activeChatId;
    const goBackToList = useCallback(() => {
        setStore((prev) => setActiveChat(prev, null));
        setAnalysis(null);
    }, []);

    const runAnalysis = async (text) => {
        if (!text || loading) return;
        setLoading(true);
        setAnalysis(null);
        // Perspective lock: detect ONCE from the original input. The same
        // perspective is reused on every regenerate so the rewrite engine
        // can never silently flip subject from You→I across the 5 tones.
        const perspective = detectPerspective(text);
        try {
            const { data } = await axios.post(`${API}/tone/analyze`, {
                message: text,
                session_id: sessionId,
                intent: intent || null,
                perspective,
            });
            // Backend doesn't echo `intent` in the response — attach it
            // here so AnalysisCard / layer1Display.resolveLoveFlag() can
            // see the active intent on this analysis (powers the Love
            // intent's closed-library lines and register modifier).
            // Also attach perspective so regenerate() can reuse it.
            setAnalysis({ ...data, intent: intent || null, perspective });
            // Brand new analysis → wipe regen counters. (Previously this
            // lived in a useEffect watching analysis?.id but that triggers
            // the set-state-in-effect anti-pattern; resetting at the
            // single point where a new analysis arrives is cleaner.)
            setRegenCountByStyle({});
            track("message_reviewed", {
                intent: intent || undefined,
                risk_level: data?.risk_level,
            });
        } catch (e) {
            console.error(e);
            toast.error("Could not analyze tone. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const checkTone = async () => {
        const text = draft.trim();
        if (!text) return;
        await runAnalysis(text);
    };

    const handleBubbleRecheck = (text) => {
        const trimmed = (text || "").trim();
        if (!trimmed || loading) return;
        // Mirror the message into the composer so the user sees what's being
        // analyzed and can keep editing afterwards.
        setDraft(trimmed);
        focusComposerToEnd(trimmed);
        runAnalysis(trimmed);
    };

    // Single-source-of-truth for "View Original" expansion. Only one bubble
    // can show its source text at a time across the entire chat.
    const [expandedOriginalId, setExpandedOriginalId] = useState(null);
    const toggleExpandedOriginal = (id) => {
        setExpandedOriginalId((curr) => (curr === id ? null : id));
    };
    // Per-style regeneration counter (max 5 per tone). Reset whenever a
    // brand new analysis arrives. Single source of truth for the strict
    // tone-lock contract — see backend regen_single_tone_with_llm.
    const [regenCountByStyle, setRegenCountByStyle] = useState({});

    const REGEN_LIMIT = 5;

    const regenerate = async (selectedToneArg) => {
        if (!analysis || regenerating) return;
        const lockedTone = selectedToneArg || analysis.versions?.[0]?.style;
        if (!lockedTone) {
            toast.error("Pick a tone tab first");
            return;
        }
        const usedSoFar = regenCountByStyle[lockedTone] || 0;
        if (usedSoFar >= REGEN_LIMIT) {
            toast.error(
                `${lockedTone[0].toUpperCase() + lockedTone.slice(1)} has 5/5 variations — try a different tone.`,
            );
            return;
        }
        const nextRegenNumber = usedSoFar + 1;

        // Avoid list = every previous text we have shown for THIS tone only.
        // Other tones stay frozen — this is what kills the "warm drift" bug.
        const avoidForLockedTone = (analysis.versions || [])
            .filter((v) => v.style === lockedTone)
            .map((v) => v.text)
            .filter(Boolean);

        setRegenerating(true);
        try {
            const { data } = await axios.post(`${API}/tone/analyze`, {
                message: analysis.original,
                session_id: sessionId,
                regenerate: true,
                // STRICT TONE LOCK — backend produces exactly 1 rewrite in
                // this style; we merge it back into the existing 5-style set.
                selected_tone: lockedTone,
                regeneration_number: nextRegenNumber,
                avoid_versions: avoidForLockedTone,
                intent: intent || null,
                // PERSPECTIVE LOCK — reuse the perspective detected at first
                // submission. Per spec: "perspective is LOCKED from original
                // message. Rewrite NEVER switches perspective." A safe
                // fallback re-detect is applied if (somehow) the analysis
                // came from a pre-perspective build.
                perspective:
                    analysis.perspective || detectPerspective(analysis.original),
                prev_signal: analysis.detected_signal,
                prev_risk: analysis.risk_level,
                prev_coaching: analysis.coaching,
                prev_label: analysis.label,
            });
            const newVersion = (data.versions || []).find(
                (v) => v.style === lockedTone,
            );
            if (!newVersion) {
                toast.error("Could not regenerate that tone — please try again.");
                return;
            }
            // Merge: replace ONLY the locked-tone entry, leave the other 4 frozen.
            // Layer 1 insight (coaching + rationale) is anchored to the ORIGINAL
            // message — NEVER overwrite it on regenerate (per display discipline
            // rule). The backend may return fresh rationale text on every regen
            // call, but we intentionally discard it here so the same insight
            // line persists across all regenerations for this message.
            setAnalysis((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    versions: (prev.versions || []).map((v) =>
                        v.style === lockedTone ? newVersion : v,
                    ),
                    // rationale / coaching intentionally NOT overridden — they
                    // are part of Layer 1 insight which is locked at first
                    // submission and reused for all subsequent regenerations.
                };
            });
            setRegenCountByStyle((c) => ({
                ...c,
                [lockedTone]: nextRegenNumber,
            }));
            toast.success(
                nextRegenNumber >= REGEN_LIMIT
                    ? `New ${lockedTone} variation (5/5)`
                    : `New ${lockedTone} variation (${nextRegenNumber}/5)`,
            );
        } catch (e) {
            console.error(e);
            const retry = e?.response?.data?.retry_after;
            toast.error(
                retry
                    ? `Rate limit — try again in ${retry}s.`
                    : "Could not regenerate. Please try again.",
            );
        } finally {
            setRegenerating(false);
        }
    };

    const pushMessage = (msg) =>
        setMessages((prev) => [
            ...prev,
            {
                id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                sender: "me",
                createdAt: new Date().toISOString(),
                ...msg,
            },
        ]);

    const sendOriginal = () => {
        if (!analysis) return;
        pushMessage({
            text: analysis.original,
            tone: analysis.tone,
            signal: analysis.detected_signal,
            risk: analysis.risk_level,
            variant: "original",
            coaching: analysis.coaching,
            toneLabel: analysis.label,
            severity: analysis.severity,
            intent: intent || null,
        });
        logRewriteSession({
            intent,
            dominantSignal: analysis.detected_signal,
            riskLevel: analysis.risk_level,
            rewriteAccepted: false,
            communicationStyle: defaultStyle,
            // Data-layer additions. None of these are surfaced anywhere yet;
            // they capture richer context for future ToneMirror layers.
            relationshipRegister: activeChat?.relationship_register || null,
            secondarySignal: analysis.secondary_signal || null,
            positiveSignalPresent: isPositiveSignal(analysis.detected_signal),
            messageText: analysis.original || draft,
        });
        setAnalysis(null);
        setDraft("");
    };

    const sendImproved = (selected) => {
        if (!analysis) return;
        const picked =
            selected ||
            analysis.versions?.[0] ||
            { text: analysis.improved, style: "concise", label: "Concise" };
        pushMessage({
            text: picked.text,
            // Rewrites are not analyzed — don't claim a tone/signal of their own.
            // We keep the original's signal under `originalSignal` so the bubble
            // can show "rewritten · from accusatory" for context.
            variant: "improved",
            style: picked.style,
            coaching: analysis.coaching,
            // Carry the original text along for the in-place "View Original"
            // toggle on the rewritten bubble. No persistence beyond what's
            // already in localStorage with the rest of the message.
            originalText: analysis.original,
            originalTone: analysis.tone,
            originalSignal: analysis.detected_signal,
            originalRisk: analysis.risk_level,
            toneLabel: analysis.label,
            severity: analysis.severity,
            intent: intent || null,
        });
        // Remember the chosen style as the user's preferred default
        if (picked.style) setPreferredStyle(picked.style);
        track("message_refined", {
            style: picked.style,
            intent: intent || undefined,
            risk_level: analysis.risk_level,
        });
        logRewriteSession({
            intent,
            dominantSignal: analysis.detected_signal,
            riskLevel: analysis.risk_level,
            rewriteAccepted: true,
            communicationStyle: defaultStyle,
            relationshipRegister: activeChat?.relationship_register || null,
            secondarySignal: analysis.secondary_signal || null,
            positiveSignalPresent: isPositiveSignal(analysis.detected_signal),
            messageText: analysis.original || draft,
        });
        setAnalysis(null);
        setDraft("");
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            checkTone();
        }
    };

    const placeholder = PLACEHOLDER_TEXT;

    // Soft word counter — emerges only as the user nears the comfort limit.
    // Below the threshold, no UI noise. Whitespace-tokenized; matches the
    // 350-word "messages, emails, difficult conversations" sweet spot.
    const wordCount = useMemo(() => {
        const t = (draft || "").trim();
        if (!t) return 0;
        return t.split(/\s+/).length;
    }, [draft]);
    const showWordHint = wordCount >= WORD_HINT_THRESHOLD;
    const overComfort = wordCount > WORD_COMFORT_LIMIT;

    // Auto-grow the composer textarea as the user types — bounded by the
    // Tailwind `max-h-72` ceiling (288px). When the content gets taller than
    // the ceiling, the textarea stops growing and a native scrollbar
    // takes over, keeping the experience conversational (not document-y).
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        // Reset to auto so shrinking back also works (e.g. after Clear).
        el.style.height = "auto";
        // 288px max comes from the `max-h-72` Tailwind class on the textarea.
        const next = Math.min(el.scrollHeight, 288);
        el.style.height = `${next}px`;
    }, [draft]);

    // Stable callbacks for the profile panel — passed into ProfilePanel's
    // `open` / click-outside effect. Re-creating these every render caused
    // the effect to subscribe-then-unsubscribe on the same tick on mobile,
    // racing with the open tap.
    const handleCloseProfile = useCallback(() => setProfileOpen(false), []);
    const handleOpenProfile = useCallback(
        () => setProfileOpen((v) => !v),
        [],
    );

    // ---------- Profile panel handlers ----------
    const handleChangeDefaultStyle = useCallback((key) => {
        // `null` = no preference (fully automatic). Any other value is a
        // user-facing style key (calm | direct | warm | professional).
        setDefaultStyleState(key || null);
        // Layer 3 + 4 of the ToneMirror intelligence system reset their
        // windows from this date so the system "ages" gracefully when the
        // user picks a different style. Layers 1 + 2 apply immediately.
        setStyleChangeDate(new Date());
        track("default_style_changed", { style: key || "auto" });
        if (!key) {
            toast("Communication style cleared");
        } else {
            const labelMap = {
                calm: "Calm & Thoughtful",
                direct: "Direct & Concise",
                warm: "Warm & Friendly",
                professional: "Professional & Neutral",
            };
            toast.success(`Style preference: ${labelMap[key] || key}`);
        }
    }, []);

    const handleChangeTonemirrorPref = useCallback((key, value) => {
        setTonemirrorPrefsState((prev) => ({ ...prev, [key]: !!value }));
    }, []);

    const handleChangeUserProfile = useCallback((patch) => {
        setUserProfileState((prev) => ({ ...prev, ...(patch || {}) }));
    }, []);

    // Mapped internal style key for the rewrite cascade — the backend
    // returns versions keyed as concise/warm/firm/professional/gentle.
    const internalDefaultStyle = defaultStyle
        ? STYLE_KEY_TO_INTERNAL[defaultStyle] || null
        : null;

    const handleExportInsights = useCallback(async () => {
        // PDF generation is async (we render HTML in an off-screen iframe,
        // snapshot it, and slice across A4 pages). Show a placeholder toast
        // so the click feels responsive, then swap to success/error.
        const tId = toast.loading("Preparing your report…");
        try {
            const blob = await buildExportPDF(store);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${exportFilenameStem(new Date())}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            track("insights_exported", { chats: store.chats.length });
            toast.success("Report downloaded", { id: tId });
        } catch {
            toast.error("Couldn't export — please try again.", { id: tId });
        }
    }, [store]);

    const handleResetAll = useCallback(() => {
        try {
            window.localStorage.removeItem("toneguard:v2");
            window.localStorage.removeItem("toneguard:v1");
            window.localStorage.removeItem("toneguard:counters");
            window.localStorage.removeItem("toneguard:shownInsightHashes");
            window.localStorage.removeItem("toneguard:privacyMode");
            // Layer 4 drift-fatigue state is anchored to the current
            // style_change_date — wiping it here keeps the reset clean.
            window.localStorage.removeItem("toneguard:layer4State");
        } catch {
            /* ignore */
        }
        // Also wipe the rewrite session log + its session-id.
        clearSessionLog();
        // Erase the server-side mirror of this user's state. Fire-and-forget
        // so the UI flips instantly — the cloud delete completes in the
        // background, and any pending debounced push is cancelled inside
        // flushCloudSync's own teardown path.
        deleteCloudState();
        setStore(loadStore());
        setAnalysis(null);
        setDraft("");
        setIntent(null);
        setDefaultStyleState(null);
        setTonemirrorPrefsState({ ...TM_DEFAULTS });
        setUserProfileState({ name: "Friend", photo: null });
        setProfileOpen(false);
        toast.success("Device data reset");
    }, []);

    return (
        <div className="tg-grain h-screen w-full bg-background flex overflow-hidden">
            {/* Sidebar — desktop always visible, mobile only when no chat selected.
                The ChatList sits in a `relative` flex-1 wrapper so the
                ProfilePanel overlay covers only the chat list area. The
                persistent ProfileTriggerRow at the bottom is a sibling of
                that wrapper and stays visible even when the overlay is open,
                so tapping the avatar a second time can close the overlay. */}
            <aside
                data-testid="chat-sidebar"
                className={`${showMobileList ? "flex" : "hidden"} md:flex flex-col w-full md:w-[320px] md:shrink-0 md:border-r md:border-border bg-card h-full min-h-0`}
            >
                <div className="relative flex-1 min-h-0 flex flex-col">
                    <ChatList
                        chats={store.chats}
                        activeId={activeChatId}
                        onSelect={handleSelectChat}
                        onNew={handleNewChat}
                        onRename={handleRenameChat}
                        onDelete={handleDeleteChat}
                        onSetRegister={handleSetChatRegister}
                        onUpdateContacts={handleUpdateChatContacts}
                        creating={creating}
                        onRequestCreate={handleRequestCreate}
                        onCloseCreate={handleCloseCreate}
                    />
                </div>
                <ProfileTriggerRow
                    userProfile={userProfile}
                    onOpenProfile={handleOpenProfile}
                />
            </aside>

            {/* Main chat area — desktop always visible, mobile only when a chat is active */}
            <div
                data-testid="chat-main"
                className={`${showMobileList ? "hidden" : "flex"} md:flex flex-col flex-1 min-w-0 h-full min-h-0`}
            >
            {activeChatId ? (
                <>
            {/* Header */}
            <header className="w-full border-b border-border/70 bg-background/80 backdrop-blur-md z-20 shrink-0">
                <div className="max-w-3xl mx-auto px-5 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="btn-back-to-list"
                            onClick={goBackToList}
                            aria-label="Back to chat list"
                            className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted -ml-1"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <div className="hidden md:flex h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 items-center justify-center">
                            <ShieldCheck className="h-4.5 w-4.5 text-primary" />
                        </div>
                        <div className="leading-tight min-w-0">
                            <h1
                                data-testid="app-title"
                                className="font-serif text-[18px] sm:text-[22px] text-foreground truncate"
                            >
                                {activeChat?.name || "ToneGuard"}
                            </h1>
                            <div className="mt-1.5">
                                <RegisterSelector
                                    value={activeChat?.relationship_register || null}
                                    onChange={(next) =>
                                        handleSetChatRegister(activeChatId, next)
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Mobile-only profile trigger — desktop accesses
                            ProfilePanel via the sidebar's ProfileTriggerRow,
                            but mobile hides the sidebar once a chat is
                            active. Keeps Profile/Preferences one tap away
                            from any chat on small screens. */}
                        <button
                            type="button"
                            data-testid="link-preferences"
                            data-tg-profile-trigger
                            onClick={handleOpenProfile}
                            aria-label="Open profile and preferences"
                            className="md:hidden inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#EAF1E8] text-[#3F523A] border border-[#D5E3D1] text-[13px] font-semibold tracking-tight font-serif overflow-hidden transition-shadow hover:shadow-[0_2px_8px_-3px_rgba(94,113,83,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E7153]/40"
                        >
                            {userProfile?.photo ? (
                                <img
                                    src={userProfile.photo}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <span aria-hidden>
                                    {(userProfile?.name || "F")
                                        .trim()
                                        .charAt(0)
                                        .toUpperCase() || "F"}
                                </span>
                            )}
                        </button>
                        <Link
                            to="/tonemirror"
                            data-testid="link-insights"
                            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground bg-transparent hover:bg-muted border border-border rounded-full px-2.5 py-1 transition-colors"
                            aria-label="Open ToneMirror"
                        >
                            <BarChart3 className="h-3 w-3" />
                            ToneMirror
                        </Link>
                        {messages.length > 0 && (
                            <button
                                data-testid="btn-clear-history"
                                onClick={clearHistory}
                                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground bg-transparent hover:bg-muted border border-border rounded-full px-2.5 py-1 transition-colors"
                                aria-label="Clear chat history"
                            >
                                <Trash2 className="h-3 w-3" />
                                Clear
                            </button>
                        )}
                        <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#5E7153]" />
                            session · {sessionId.slice(-6)}
                        </div>
                    </div>
                </div>
            </header>

            {/* Chat feed — flex-grows and scrolls INTERNALLY so message bubbles
                stay strictly above the Intent/composer anchor and never bleed
                under it. */}
            <main className="flex-1 min-h-0 w-full overflow-y-auto">
                <div
                    ref={scrollRef}
                    data-testid="chat-feed"
                    className="max-w-3xl mx-auto px-5 sm:px-6 py-3 sm:py-8 pb-4 sm:pb-6 flex flex-col gap-4"
                >
                    {messages.length === 0 && !analysis && (
                        <EmptyState
                            hasDraft={draft.trim().length > 0}
                            rotateKey={`${activeChatId || "none"}:${microLineToken}`}
                            firstName={(userProfile?.name || "").trim().split(/\s+/)[0] || null}
                        />
                    )}

                    {(() => {
                        // Inject a date divider whenever the calendar day changes.
                        let lastDay = null;
                        const out = [];
                        for (const m of messages) {
                            const k = dayKeyFor(m.createdAt);
                            if (k !== lastDay) {
                                lastDay = k;
                                out.push(
                                    <DateDivider
                                        key={`d-${k}-${m.id}`}
                                        label={dayLabelFor(m.createdAt)}
                                    />,
                                );
                            }
                            out.push(
                                <ChatBubble
                                    key={m.id}
                                    message={m}
                                    onCopy={handleBubbleCopy}
                                    onEdit={handleBubbleEdit}
                                    onDelete={handleBubbleDelete}
                                    onRecheck={handleBubbleRecheck}
                                    expandedOriginal={expandedOriginalId === m.id}
                                    onToggleOriginal={toggleExpandedOriginal}
                                    showRewriteComparison={!!tonemirrorPrefs.rewriteComparison}
                                    showEmotionalNudges={!!tonemirrorPrefs.emotionalNudges}
                                />,
                            );
                        }
                        return out;
                    })()}

                    {analysis && (
                        <AnalysisCard
                            analysis={analysis}
                            onSendOriginal={sendOriginal}
                            onUseImproved={sendImproved}
                            onDismiss={() => {
                                // Log this completed-rewrite as not-accepted.
                                // Capture the analysis fields BEFORE we wipe state.
                                if (analysis) {
                                    logRewriteSession({
                                        intent,
                                        dominantSignal: analysis.detected_signal,
                                        riskLevel: analysis.risk_level,
                                        rewriteAccepted: false,
                                        communicationStyle: defaultStyle,
                                        relationshipRegister:
                                            activeChat?.relationship_register || null,
                                        secondarySignal:
                                            analysis.secondary_signal || null,
                                        positiveSignalPresent: isPositiveSignal(
                                            analysis.detected_signal,
                                        ),
                                        messageText: analysis.original || draft,
                                    });
                                }
                                setAnalysis(null);
                                // A user closing the rewrite without using
                                // it is one of ToneGuard's most reflective
                                // moments — refresh the micro-line so a
                                // new gentle reminder fades in.
                                setMicroLineToken((t) => t + 1);
                            }}
                            onRegenerate={regenerate}
                            regenerating={regenerating}
                            preferredStyle={preferredStyle}
                            defaultStyle={internalDefaultStyle}
                            userStyle={defaultStyle}
                            chatId={activeChatId}
                            chatRegister={activeChat?.relationship_register || null}
                            activeChat={activeChat}
                            intent={intent}
                            regenCountByStyle={regenCountByStyle}
                            regenLimit={REGEN_LIMIT}
                        />
                    )}

                    {loading && <AnalyzingIndicator />}
                </div>
            </main>

            {/* Composer / Intent anchor — strict bottom boundary.

                Mobile (< sm) implements a state-driven WhatsApp-like flow:
                  · Idle / Focused (no text): chips visible above input,
                    mic icon visible inside input row, no Review CTA.
                  · Typing (≥ 1 char): chips hidden, mic swaps to a
                    sparkle send-style "Review" button in the same slot
                    (no layout shift), an "Apologize ✓"-style intent badge
                    appears above the input if the user picked one.
                Desktop (≥ sm) stays static: chips always visible, mic
                always visible, inline "Review message" pill always shown
                via `hidden sm:inline-flex`. */}
            <footer className="shrink-0 z-20 w-full bg-background border-t border-border/70">
                <div className="max-w-3xl mx-auto px-5 sm:px-6 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:pb-6 pt-2 sm:pt-3">
                    {/* Progressive intent disclosure — sits as its own
                        calm tray ABOVE the input. Default: a single "Intent"
                        pill. Expanded: a clean card with a header row (the
                        toggle word + optional tone-default chip on the right)
                        and a horizontally-scrolling chip strip; optional
                        suggestion starters below. The whole tray collapses
                        on mobile while typing (preserved). */}
                    <div
                        data-testid="intent-disclosure"
                        data-expanded={intentExpanded}
                        className={`overflow-hidden sm:overflow-visible sm:block transition-[max-height,opacity] duration-200 ease-out ${
                            isTyping
                                ? "max-h-0 opacity-0 sm:opacity-100 sm:max-h-none"
                                : "max-h-72 opacity-100"
                        }`}
                    >
                        {!intentExpanded ? (
                            (() => {
                                const meta = intent
                                    ? INTENTS.find((i) => i.key === intent)
                                    : null;
                                const label = meta ? meta.label : "Intent";
                                return (
                                    <div className="mb-2 flex">
                                        <button
                                            type="button"
                                            data-testid="btn-intent-toggle"
                                            data-active={!!intent}
                                            aria-expanded={false}
                                            aria-label={
                                                intent
                                                    ? `Intent: ${label}. Tap to change.`
                                                    : "Choose an intent"
                                            }
                                            onClick={() => setIntentExpanded(true)}
                                            className={`shrink-0 inline-flex items-center gap-1.5 text-[12px] font-medium rounded-full px-3 py-1.5 border transition-colors ${
                                                intent
                                                    ? "bg-[#5E7153] text-white border-[#5E7153] shadow-sm"
                                                    : "bg-card text-foreground/70 border-border hover:text-foreground hover:bg-muted"
                                            }`}
                                        >
                                            {intent && (
                                                <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
                                            )}
                                            <span className="text-[10px] uppercase tracking-[0.14em] opacity-80 mr-0.5">
                                                Intent
                                            </span>
                                            {intent && (
                                                <span className="opacity-95">
                                                    · {label}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                );
                            })()
                        ) : (
                            <div
                                data-testid="intent-tray"
                                className="mb-2 rounded-2xl border border-border/60 bg-card/80 backdrop-blur-[2px] px-3 py-2.5"
                            >
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <button
                                        type="button"
                                        data-testid="btn-intent-toggle"
                                        data-active={!!intent}
                                        aria-expanded={true}
                                        aria-label="Hide intent options"
                                        onClick={() => setIntentExpanded(false)}
                                        className="shrink-0 inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground/80 hover:text-foreground rounded-full transition-colors"
                                    >
                                        Intent
                                    </button>
                                    {/* Inline tone-default hint — anchors
                                        next to the toggle instead of
                                        floating orphan below the input. */}
                                    {intent && (() => {
                                        const styleKey =
                                            preferredStyle && [
                                                "concise","warm","firm","professional","gentle",
                                            ].includes(preferredStyle)
                                                ? preferredStyle
                                                : INTENT_TO_STYLE[intent];
                                        if (!styleKey) return null;
                                        const styleLabel = STYLE_LABELS[styleKey] || styleKey;
                                        return (
                                            <span
                                                data-testid="default-tone-inline"
                                                className="inline-flex items-center gap-1 text-[10.5px] text-[#4B6143] font-medium"
                                            >
                                                <span className="h-1 w-1 rounded-full bg-[#5E7153]" />
                                                Tone:{" "}
                                                <span className="font-semibold">
                                                    {styleLabel}
                                                </span>
                                            </span>
                                        );
                                    })()}
                                </div>
                                <div
                                    data-testid="intent-chips-wrapper"
                                    data-mobile-visible={!isTyping}
                                >
                                    <IntentPresets
                                        value={intent}
                                        onChange={handleIntentChange}
                                    />
                                </div>
                                {startersVisible && (
                                    <div className="mt-2 pt-2 border-t border-border/40">
                                        <IntentStarters
                                            intent={intent}
                                            onPick={pickStarter}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Mobile-only: subtle "Apologize ✓" badge above input
                        when typing AND an intent is selected. Keeps the
                        chosen context visible without cluttering the row. */}
                    {isTyping && intent && (() => {
                        const meta = INTENTS.find((i) => i.key === intent);
                        if (!meta) return null;
                        return (
                            <div
                                data-testid="selected-intent-badge"
                                data-intent={intent}
                                className="sm:hidden mb-2 flex"
                            >
                                <button
                                    type="button"
                                    onClick={() => handleIntentChange(null)}
                                    className="inline-flex items-center gap-1 text-[11px] font-medium text-[#4B6143] bg-[#EAF1E8]/80 border border-[#D5E3D1] rounded-full px-2.5 py-1 transition-colors hover:bg-[#EAF1E8]"
                                    aria-label={`Clear intent ${meta.label}`}
                                >
                                    <span className="h-1 w-1 rounded-full bg-[#5E7153]" />
                                    {meta.label} <span aria-hidden>✓</span>
                                </button>
                            </div>
                        );
                    })()}
                    <div className="rounded-3xl bg-card border border-border shadow-[0_-8px_30px_-15px_rgba(0,0,0,0.08)] px-2.5 py-2 sm:p-3.5 flex items-end gap-1.5 sm:gap-2">
                        <Textarea
                            data-testid="chat-input"
                            ref={inputRef}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder}
                            rows={1}
                            maxLength={MAX_INPUT_CHARS}
                            className="resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-[15px] min-h-[44px] max-h-72 py-2.5 flex-1 min-w-0"
                        />
                        {/* MOBILE: mic in idle, send-style CTA when typing.
                            Both occupy a fixed `h-9 w-9` slot so the swap is
                            layout-stable. The CTA is rendered the moment
                            `isTyping` becomes true with NO entrance animation
                            (only a `transition-opacity` once mounted). This
                            guarantees the CTA is reachable even if the
                            browser disables animations or runs reduced-motion. */}
                        {voice.isSupported && !isTyping && (
                            <div className="sm:hidden">
                                <MicButton
                                    isRecording={voice.isRecording}
                                    onToggle={toggleVoice}
                                    disabled={loading}
                                />
                            </div>
                        )}
                        {isTyping && (
                            <button
                                type="button"
                                data-testid="btn-check-tone-mobile"
                                onClick={checkTone}
                                disabled={loading}
                                aria-label={loading ? "Reviewing message" : "Review message"}
                                className="sm:hidden inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-opacity duration-150 opacity-100"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Sparkles className="h-4 w-4" />
                                )}
                            </button>
                        )}
                        {/* DESKTOP: mic + Review pill always present. */}
                        {voice.isSupported && (
                            <div className="hidden sm:flex sm:items-end">
                                <MicButton
                                    isRecording={voice.isRecording}
                                    onToggle={toggleVoice}
                                    disabled={loading}
                                />
                            </div>
                        )}
                        <Button
                            data-testid="btn-check-tone"
                            onClick={checkTone}
                            disabled={!canAnalyze}
                            className="hidden sm:inline-flex rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 gap-2 px-5 h-10 text-sm"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Reviewing
                                </>
                            ) : (
                                <>
                                    <Sparkles className="h-4 w-4" />
                                    Review message
                                </>
                            )}
                        </Button>
                    </div>
                    {showWordHint && (
                        <div
                            data-testid="word-count-hint"
                            data-over-comfort={overComfort}
                            aria-live="polite"
                            className={`mt-1.5 px-2 text-[11px] tabular-nums flex items-center justify-end gap-1 transition-colors ${
                                overComfort
                                    ? "text-[#A35741]"
                                    : "text-muted-foreground/80"
                            }`}
                        >
                            <span>
                                {wordCount} / {WORD_COMFORT_LIMIT} words
                            </span>
                            {overComfort && (
                                <span className="text-[10px]">
                                    · Shorter messages get sharper rewrites
                                </span>
                            )}
                        </div>
                    )}
                    {voice.isRecording && (
                        <div
                            data-testid="voice-listening-indicator"
                            role="status"
                            aria-live="polite"
                            className="tg-enter mt-2 flex items-center justify-center gap-2 text-[11px] font-medium text-[#A35741]"
                        >
                            <span className="relative inline-flex h-1.5 w-1.5">
                                <span className="absolute inset-0 rounded-full bg-[#A35741] animate-ping opacity-70" />
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#A35741]" />
                            </span>
                            Listening…
                        </div>
                    )}
                    {intent && (() => {
                        const styleKey =
                            (preferredStyle && [
                                "concise","warm","firm","professional","gentle",
                            ].includes(preferredStyle)
                                ? preferredStyle
                                : INTENT_TO_STYLE[intent]);
                        if (!styleKey) return null;
                        const styleLabel = STYLE_LABELS[styleKey] || styleKey;
                        const isPinned = preferredStyle === styleKey;
                        return (
                            <div
                                data-testid="default-tone-pill"
                                data-style={styleKey}
                                data-source={isPinned ? "preferred" : "intent"}
                                className="tg-enter mt-2 hidden sm:flex justify-center"
                            >
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#4B6143] bg-[#EAF1E8]/70 border border-[#D5E3D1] rounded-full px-2.5 py-1">
                                    <span className="h-1 w-1 rounded-full bg-[#5E7153]" />
                                    Tone will default to{" "}
                                    <span className="font-semibold">
                                        {styleLabel}
                                    </span>
                                    {isPinned && (
                                        <span className="text-[10px] uppercase tracking-[0.08em] text-[#4B6143]/70 font-semibold ml-0.5">
                                            · saved
                                        </span>
                                    )}
                                </span>
                            </div>
                        );
                    })()}
                    {/* Per-chat tone memory — only when no intent is picked
                        AND the chat has a remembered style from a past
                        rewrite. Disappears as soon as the user picks an
                        intent (the existing default-tone-pill above takes
                        over) or starts typing on mobile. */}
                    {!intent && !isTyping && preferredStyle && (() => {
                        const styleLabel = STYLE_LABELS[preferredStyle] || preferredStyle;
                        return (
                            <div
                                data-testid="chat-tone-memory"
                                data-style={preferredStyle}
                                className="tg-enter mt-2 flex justify-center"
                            >
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground bg-muted/40 border border-border rounded-full px-2.5 py-1">
                                    <span className="h-1 w-1 rounded-full bg-[#5E7153]" />
                                    Last time you used{" "}
                                    <span className="font-semibold text-[#4B6143]">
                                        {styleLabel}
                                    </span>{" "}
                                    here
                                </span>
                            </div>
                        );
                    })()}
                    <MicroLine
                        variant="footer"
                        rotateKey={`${activeChatId || "none"}:${microLineToken}`}
                        className="mt-2.5 mb-1"
                    />
                </div>
            </footer>
                </>
            ) : (
                <DesktopEmptyState onRequestCreate={handleRequestCreate} />
            )}
            </div>

            {/* Profile Panel — rendered at root so its mobile bottom-sheet
                variant is reachable when the sidebar is hidden (i.e. once
                a chat is active on mobile). The desktop variant uses fixed
                positioning anchored to the 320px sidebar so it visually
                overlays the chat list area exactly as before. */}
            <ProfilePanel
                open={profileOpen}
                onClose={handleCloseProfile}
                userProfile={userProfile}
                onChangeUserProfile={handleChangeUserProfile}
                defaultStyle={defaultStyle}
                onChangeDefaultStyle={handleChangeDefaultStyle}
                tonemirrorPrefs={tonemirrorPrefs}
                onChangeTonemirrorPref={handleChangeTonemirrorPref}
                soundEnabled={soundEnabled}
                onToggleSound={(next) => {
                    setSoundEnabled(next);
                    toast(
                        next
                            ? "Sound on — gentle pad while reviewing"
                            : "Sound off",
                    );
                }}
                onResetAll={handleResetAll}
                onLogout={authLogout}
                accountEmail={authUser?.email || null}
                onExport={handleExportInsights}
                chatsCount={store.chats.length}
            />
        </div>
    );
};

/**
 * Desktop-only placeholder shown when the user has the sidebar open but
 * no chat is selected (e.g. they just deleted the last one).
 */
const DesktopEmptyState = ({ onRequestCreate }) => (
    <div
        data-testid="desktop-empty-state"
        className="hidden md:flex flex-1 items-center justify-center px-6 py-12"
    >
        <div className="max-w-md text-center">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5">
                <ShieldCheck className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-serif text-3xl text-foreground leading-tight">
                Pick a chat to begin
            </h2>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Each chat keeps the drafts for one person — Manager, Mom, Sam.
                Pick one from the left or start a new one.
            </p>
            <Button
                data-testid="btn-desktop-new-chat"
                onClick={() => onRequestCreate?.()}
                className="mt-5 rounded-full gap-2"
            >
                <Sparkles className="h-4 w-4" />
                Start a new chat
            </Button>
        </div>
    </div>
);

const EmptyState = ({ hasDraft = false, rotateKey, firstName = null }) => (
    <div
        data-testid="empty-state"
        className="tg-enter mx-auto text-center py-4 sm:py-16 max-w-md"
    >
        <div className="mx-auto h-10 w-10 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-2 sm:mb-5">
            <Sparkles className="h-4 w-4 sm:h-6 sm:w-6 text-primary" />
        </div>
        {firstName && firstName.toLowerCase() !== "friend" && (
            <p
                data-testid="empty-state-welcome"
                className="text-sm sm:text-base text-muted-foreground mb-2"
            >
                Welcome, <span className="text-foreground font-medium">{firstName}</span>.
            </p>
        )}
        <h2 className="font-serif text-lg sm:text-3xl text-foreground leading-tight">
            Pause. Rewrite. Send with care.
        </h2>
        {/* Description — hidden on mobile to keep the hero compact
            (spec §2, max 80–100px). On desktop it stays in place. */}
        <p
            className={`text-sm text-muted-foreground mt-3 leading-relaxed ${
                hasDraft ? "hidden" : "hidden sm:block"
            }`}
        >
            Draft a message below and tap{" "}
            <span className="text-foreground font-medium">Review message</span>.
        </p>
        <MicroLine
            variant="empty"
            rotateKey={rotateKey}
            className={`mt-4 sm:mt-5 ${hasDraft ? "hidden sm:block" : ""}`}
        />
    </div>
);

export default ToneGuardPage;
